import React, { useEffect, useState } from 'react';
import { Card, Col, Row, Statistic, Progress, Alert } from 'antd';
import { getMonitorStats } from '../services/monitor';
import type { MonitorStats } from '../services/monitor';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { ClockCircleOutlined, DesktopOutlined, ThunderboltOutlined } from '@ant-design/icons';

interface HistoryPoint {
  time: string;
  activeRequests: number;
  avgResponseTime: number;
  memoryUsage: number;
}

const Monitor: React.FC = () => {
  const [stats, setStats] = useState<MonitorStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [history, setHistory] = useState<HistoryPoint[]>([]);

  const fetchStats = async () => {
    try {
      const data = await getMonitorStats();
      setStats(data);
      setError(null);

      // 更新历史数据用于图表
      const now = new Date();
      const timeStr = `${now.getHours()}:${now.getMinutes()}:${now.getSeconds()}`;
      
      setHistory(prev => {
        const newData = [
          ...prev,
          {
            time: timeStr,
            activeRequests: data.app.activeRequests,
            avgResponseTime: data.app.avgResponseTime,
            memoryUsage: data.memory.usagePercentage,
          }
        ];
        // 只保留最近20个点
        return newData.slice(-20);
      });
    } catch (err) {
      console.error('Failed to fetch monitor stats:', err);
      setError('无法连接到监控服务');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let timer: number | null = null;
    const start = () => {
      if (timer !== null) return;
      void fetchStats();
      timer = window.setInterval(fetchStats, 10000);
    };
    const stop = () => {
      if (timer === null) return;
      window.clearInterval(timer);
      timer = null;
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') start();
      else stop();
    };

    start();
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  if (loading && !stats) {
    return <Card loading={true} />;
  }

  if (error && !stats) {
    return <Alert message="监控服务不可用" description={error} type="error" showIcon />;
  }

  if (!stats) return null;

  return (
    <div style={{ padding: '24px' }}>
      <h2 style={{ marginBottom: '24px' }}>服务器实时监控</h2>

      {/* 系统信息概览 */}
      <Card style={{ marginBottom: '24px' }} title="系统信息">
        <Row gutter={16}>
          <Col span={6}>
            <Statistic title="主机名" value={stats.os.hostname} prefix={<DesktopOutlined />} />
          </Col>
          <Col span={6}>
            <Statistic title="操作系统" value={`${stats.os.platform} (${stats.os.arch})`} />
          </Col>
          <Col span={6}>
            <Statistic 
              title="系统运行时间" 
              value={Math.floor(stats.os.uptime / 3600)} 
              suffix="小时" 
              prefix={<ClockCircleOutlined />}
            />
          </Col>
          <Col span={6}>
            <Statistic 
              title="应用运行时间" 
              value={Math.floor(stats.process.uptime / 3600)} 
              suffix="小时" 
              prefix={<ClockCircleOutlined />}
            />
          </Col>
        </Row>
      </Card>

      {/* 核心指标 */}
      <Row gutter={16} style={{ marginBottom: '24px' }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="当前并发请求"
              value={stats.app.activeRequests}
              valueStyle={{ color: stats.app.activeRequests > 10 ? '#cf1322' : '#3f8600' }}
              prefix={<ThunderboltOutlined />}
            />
            <div style={{ marginTop: 8, color: '#8c8c8c' }}>
              实时处理中的请求数
            </div>
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="平均响应时间"
              value={stats.app.avgResponseTime}
              suffix="ms"
              valueStyle={{ color: stats.app.avgResponseTime > 500 ? '#cf1322' : '#3f8600' }}
            />
            <div style={{ marginTop: 8, color: '#8c8c8c' }}>
              最近请求平均耗时
            </div>
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="总请求数"
              value={stats.app.totalRequests}
            />
            <div style={{ marginTop: 8, color: '#8c8c8c' }}>
              自启动以来的总流量
            </div>
          </Card>
        </Col>
        <Col span={6}>
          <Card title="内存使用率">
            <Row align="middle" gutter={16}>
              <Col span={16}>
                <Progress percent={stats.memory.usagePercentage} status={stats.memory.usagePercentage > 80 ? 'exception' : 'active'} />
              </Col>
              <Col span={8}>
                <div style={{ fontSize: '12px' }}>{stats.memory.used} / {stats.memory.total}</div>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      {/* 图表区域 */}
      <Row gutter={16}>
        <Col span={12}>
          <Card title="响应时间趋势 (ms)">
            <div style={{ height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={history}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="time" />
                  <YAxis />
                  <Tooltip />
                  <Line type="monotone" dataKey="avgResponseTime" stroke="#8884d8" isAnimationActive={false} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </Col>
        <Col span={12}>
          <Card title="并发请求趋势">
            <div style={{ height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={history}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="time" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Line type="monotone" dataKey="activeRequests" stroke="#82ca9d" isAnimationActive={false} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Monitor;
